import _ from "lodash";
import { mapActions } from "vuex";
import { bus } from "@/main";
import { Point } from "ol/geom";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export const dynamicObjectFormsMixin = {
  mixins: [checkPermissionsMixin],

  props: {
    /**
     * Type of action desired when opening the form.
     */
    actionType: {
      type: String,
      default: "edit"
    },

    /**
     * Name of the layer of the object for which the form was opened.
     */
    objectLayer: {
      type: String,
      required: true
    },
    /**
     *Object data, in case the form is opened for a previously existing object.
     */
    dataReceived: {
      type: Object,
      required: false
    },
    /**
     *Type of geometry and coordinates of the objectData to create (in Well-known Text).
     */
    wkt: {
      type: [String, Object],
      required: false
    },
    /**
     * If the Street View is open or not.
     */
    streetView: {
      type: Boolean,
      default: false
    },

    previousPosX: {
      type: Number
    },

    previousPosY: {
      type: Number
    }
  },

  data() {
    return {
      loadingStructure: true,
      loadingSave: false,

      form: {
        type_name: undefined,
        endpoint: undefined,
        initials: undefined,
        permission: undefined,
        name_translation: undefined,
        local_storage_name: undefined,
        tabs: []
      },
      customFields: [],
      activeTab: 0,

      required: {
        fields: [],
        filled: true,
        stillNeedFilled: []
      },

      objectData: {},
      dataToSend: {
        custom_fields: {},
      },

      imagesList: [],
      previousImages: [],
      imagesToRemove: [],

      materialOptions: null,

      updatedSplicingConnections: null,
      initialConnections: null,
      dataWasChanged: false,

      accessoriesSelected: [],
      othersSelected: [],
      accessoriesSelectedByMaterialId: [],
      othersSelectedByMaterialId: []
    };
  },

  computed: {
    /**
     * Determines if the object is editable or not.
     * @returns {boolean}
     */
    readOnly() {
      return (
        this.actionType === "view" ||
        this.actionType === "splicing" ||
        this.actionType === "activities" ||
        this.loadingSave
      );
    },

    /**
     * Stores the initials corresponding to the current object layer.
     * @returns {string}
     */
    initials() {
      return this.form.initials + "_";
    },

    /**
     * Determines if the form of the current object ype has a photos tab or not.
     * @returns {boolean}
     */
    hasPhotosTab() {
      return this.form.tabs.some(tab => tab.type === "photos");
    },
    /**
     * Activities tab and CustomFields tab are added separatly from form.tabs, sets which tabs are visible to the user.
     * @returns {Array} - All visibletabs and its properties.
     */

    visibleTabs() {
      let visibleTabs = [];

      for (let tab of this.form.tabs) {
        if (this.tabIsVisible(tab)) {
          tab.enabled = this.tabIsEnabled(tab);
          visibleTabs.push(tab);
        }
      }

      if (this.objectLayer !== 'fc_virtual') {
        this.includeActivitiesTab(visibleTabs);
      }

      if (this.objectLayer !== "fc_asset_reservation") {
        this.includeCustomFieldsTab(visibleTabs);
      }
      if(this.objectData.hasOwnProperty('cl_logical_type') && this.objectData.cl_logical_type === 'FB'){
        visibleTabs = [...visibleTabs.filter(item => item.code === 'closure_splitters' || item.code === 'closure_splicing')]
      }
      return visibleTabs;
    },

    xCoordinate() {
      return this.getCoordinate(0);
    },

    yCoordinate() {
      return this.getCoordinate(1);
    },

    surfaceTypeIsOther() {
      let field = this.getVarNameSurface();
      return this.objectData[field] === "11";
    },

    surfaceTypeIsCarriageway() {
      let field = this.getVarNameSurface();
      return this.objectData[field] === "1";
    },

    materialRequestQueryParameters() {
      return {};
    },

    dataWasUpdated() {
      return (
        Object.keys(this.dataToSend).length > 0 ||
        this.imagesToRemove.length > 0 ||
        this.updatedSplicingConnections ||
        this.accessoriesSelected.length > 0 ||
        this.othersSelected.length > 0
      );
    },

    objectUUID() {
      if (this.dataReceived) {
        return this.dataReceived[this.initials + "uuid"];
      } else {
        return null;
      }
    }
  },

  watch: {
    actionType() {
      if (this.actionType === "edit") {
        this.watchObjectValueChanges();
      }
    },

    activeTab() {
      this.$nextTick(() => {
        this.focusFirstInput();
      });
    },

    loadingStructure(newValue) {
      if (this.actionType === "activities") {
        let tabRedirect = this.visibleTabs.findIndex(tab =>
          tab.label.includes("Activities")
        );
        if (tabRedirect > -1) {
          this.activeTab = tabRedirect;
        }
      }
      if (!newValue) {
        this.objectData.coordinate_x = this.xCoordinate;
        this.objectData.coordinate_y = this.yCoordinate;
        this.getPreviousImages();
      }
    },

    surfaceTypeIsOther() {
      let varName = this.initials + "surface_type_other";
      let fields = this.form.tabs[0].fields;
      this.updateDependentFieldValueAndDisabledState(
        varName,
        this.surfaceTypeIsOther,
        fields
      );
    },

    surfaceTypeIsCarriageway() {
      let varName = "extra_fields.road_type";
      let fields = this.form.tabs[0].fields;
      this.updateDependentFieldValueAndDisabledState(
        varName,
        this.surfaceTypeIsCarriageway,
        fields
      );
    },

    "required.fields"(){
      this.checkRequiredFieldsAreFilled();
    }
  },

  created() {
    this.structureAndPopulateForm();
  },

  methods: {
    ...mapActions([
      "CREATE_OBJECTS",
      "EDIT_OBJECTS",
      "DELETE_PHOTO",
      "GET_AUTOMATIC_LABEL",
      "BEST_ROUTE_FINAL",
      "fetchStockMaterialsList",
      "GET_STOCK_MATERIAL",
      "FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE",
      "GET_LIST",
      "GET_LIST_FILTERED",
      "updateObjectSplicingConnections",
      "fetchWorkspaceByUUID",
      "setTabDisabled",
      "GET_LAYER_ATTRIBUTES",
      "getAllObject",
      "FETCH_ALL_NETWORK_RULES_TEMPLATES"
    ]),

    /////////////////////////////////////////
    /* Form Structure and Populate Methods */
    /////////////////////////////////////////

    /**
     * Gets the structure of the form from the backend and populates the objectData object.
     */
    async structureAndPopulateForm() {
      this.loadingStructure = true;
      this.form = await this.FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE({
        layer: this.objectLayer,
        needsTabs: true,
        needsFields: true
      });

      this.customFields = await this.GET_LAYER_ATTRIBUTES({
        layer: this.objectLayer
      });

      if (this.hasPhotosTab) {
        this.getImagesList();
      }

      if (this.actionType === "create") {
        await this.populateObject();
      } else {
        if (this.dataReceived) {
          this.objectData = JSON.parse(JSON.stringify(this.dataReceived));
          if (!this.objectData.custom_fields) {
            this.objectData.custom_fields = {};
          }
          if (!this.objectData.specific_fields) {
            this.objectData.specific_fields = {};
          }
        }

        if (this.objectLayer === "fc_blockage") {
          this.dataToSend.specific_fields = {};
        }
        if (this.dataReceived.accessories) {
          this.accessoriesSelected = [];
          this.othersSelected = [];
          this.generateAccessoriesAndOthersSelected(
            this.accessoriesSelected,
            this.othersSelected,
            this.objectData.accessories
          );
        }

        this.loadingStructure = false;
        this.$nextTick(() => this.focusFirstInput());

        if (this.actionType === "edit") {
          for (const required of this.form.tabs) {
            await this.addFormTabFieldsToObject(
              required.fields,
              this.objectData
            );
          }
          if (this.customFields && this.customFields.custom_fields) {
            for (const field of this.customFields.custom_fields) {
              this.registerRequiredField(field);
            }
          }

          this.watchObjectValueChanges();
        }
      }
      if (this.actionType === "createAssetReservation") {
        this.objectData.ar_created_at = null;
        this.objectData.ar_updated_at = null;
        this.objectData.ar_deleted_at = null;
      }
      if (this.objectLayer === 'fc_cable' && !this.sameCableTypeAsLastCreated() && this.actionType === 'create') {
        this.objectData.cb_material_id = null;
      }
      this.dataWasChanged = false;
    },

    sameCableTypeAsLastCreated() {
      let lastCable = window.sessionStorage.getItem("lastCable");
      return lastCable
        ? parseInt(JSON.parse(lastCable).cb_logical_type) === parseInt(this.objectData.cb_logical_type)
        : false;
    },

    /**
     * Populates the objectData with the variables used in the give form structure.
     */
    async populateObject() {
      let formStructure = Array.from(this.form.tabs);
      let nrTabs = formStructure.length;
      let object = {};
      if (nrTabs > 0) {
        object.custom_fields = {};
        if (this.objectLayer === "fc_blockage") {
          object.specific_fields = {};
          this.dataToSend.specific_fields = {};
        }
        for (const tab of formStructure) {
          if (tab.type === "form") {
            await this.addFormTabFieldsToObject(tab.fields, object);
          } else if (tab.type === "photos") {
            object.images = [];
          }
        }
        if (this.customFields && this.customFields.custom_fields) {
          await this.addFormTabFieldsToObject(
            this.customFields.custom_fields,
            object.custom_fields
          );
        }
      }

      this.objectData = object;
      this.loadingStructure = false;
      this.$nextTick(() => this.focusFirstInput());
      this.checkRequiredFieldsAreFilled();
    },

    /**
     * Add each field of a tab of type form to the objectData object
     * @param {array} tabFields - list of fields of a given tab
     * @param {Object} object
     */
    async addFormTabFieldsToObject(tabFields, object) {
      if (tabFields.length > 0) {
        for (const field of tabFields) {
          this.registerRequiredField(field);

          if (field.checkbox_options && field.checkbox_options.length !== 0) {
            for (const checkboxOption of field.checkbox_options) {
              await this.registerField(object, field, checkboxOption);
            }
          } else if (!field.var_name) {
            this.registerCustomFields(object, field);
          } else {
            await this.registerField(object, field, null);
          }
        }
      }
    },

    /**
     *
     * @param {Object} object
     * @param {Object} field
     * Registers Custom Fields with its default_values
     */
    registerCustomFields(object, field) {
      let value;
      let localStorageData = JSON.parse(
        sessionStorage.getItem(this.form.local_storage_name)
      );

      let localStorageValue;
      if (localStorageData) {
        localStorageValue = _.get(localStorageData.custom_fields, field.uuid);
      }

      if (localStorageValue) {
        value = localStorageValue;
      } else if (field.default_value) {
        value = field.default_value;
      }

      _.set(object, field.uuid, value);
    },

    /**
     * Register field in object
     * @param {Object} field
     * @param {Object} object
     * @param {Object} checkboxOption
     */
    async registerField(object, field, checkboxOption) {
      let localStorageData = JSON.parse(
        sessionStorage.getItem(this.form.local_storage_name)
      );

      let var_name = checkboxOption ? checkboxOption.var_name : field.var_name;
      let value;

      if (this.actionType === "create") {
        let localStorageValue;

        if (localStorageData) {
          localStorageValue = _.get(localStorageData, var_name);
        }

        if (localStorageValue) {
          value = localStorageValue;
        } else {
          if (
            var_name === this.initials + "demand_point_status" ||
            var_name === this.initials + "status"
          ) {
            value = await this.getWorkspacePhaseValue();
          } else if (field.default_value !== null) {
            value = field.default_value;
          } else if (field.input_type !== "checkbox") {
            value = undefined;
          } else {
            value = false;
          }
        }

        if (this.fieldCanBeAutofilled(var_name)) {
          _.set(object, var_name, value);
        } else {
          _.set(object, var_name, null);
        }
      }
    },

    /**
     * Register field in the required list
     * @param {Object} field
     */
    registerRequiredField(field) {
      if (field.required && !field.hide) {
        this.required.fields.push(field);
      }
    },

    /**
     * Fills the objectData to send to the server by putting a watcher in each variable of the objectData.
     */
    watchObjectValueChanges() {
      if (this.objectData.hasOwnProperty("images")) {
        this.watchImages();
      }
    },

    watchImages() {
      this.$watch("objectData.images", updatedImages => {
        let imagesToSend = [];
        for (const image of updatedImages) {
          if (image.hasOwnProperty("image")) {
            imagesToSend.push(image);
          } else if (image.hasOwnProperty("description")) {
            let updatedImage = {
              description: image.description,
              name: image.name
            };
            imagesToSend.push(updatedImage);
          }
        }
        this.dataToSend.images = imagesToSend;
      });
    },

    /**
     * Fills the dataToSend with objectData data.
     */
    placeObjectDataInDataToSend() {
      for (const key of Object.keys(this.objectData)) {
        if (key.includes(this.initials)) {
          let newKey = key.replace(this.initials, "");
          this.dataToSend[newKey] = this.objectData[key];
        }
        if (
          key === "images" ||
          key === "extra_fields" ||
          key === "custom_fields" ||
          key === "specific_fields"
        ) {
          this.dataToSend[key] = this.objectData[key];
        }
        if (key === "address") {
          let address = {};
          for (const key of Object.keys(this.objectData.address)) {
            let newKey = key.replace("ad_", "");
            if (this.objectData.address[key]) {
              address[newKey] = this.objectData.address[key];
            }
          }
          if (Object.keys(address).length > 0) {
            this.dataToSend.address = address;
          }
        }
      }
    },

    /**
     * Gets the (nested) objectData corresponding to a string key, with a new value
     * @param {String} varName - path to the variable we're trying to access.
     */
    getValueInObject(varName, inputType = null, type = null) {
      if (inputType === "photo") {
        return this.getPhoto(varName);
      } else {
        if (type && type === "customfield") {
          return _.get(this.objectData.custom_fields, varName);
        }
        if (type && type === "blockage_specific_fields") {
          return _.get(this.objectData.specific_fields, varName);
        }
        if(varName === "ar_internal_id"){
          this.internalReference = _.get(this.objectData, varName);
        }
        return _.get(this.objectData, varName);
      }
    },

    getPhoto(imageName) {
      if (this.objectData && this.objectData.images) {
        let photo = this.objectData.images.find(image => {
          return image.name === imageName;
        });
        if (!photo) {
          photo = {
            name: "",
            image: "",
            image_thumb: "",
            description: ""
          };
        }
        return photo;
      }
    },

    /**
     * Sets the  (nested) objectData corresponding to a string key, with a new value
     * @param {String} varName - path to the variable we're trying to access.
     * @param {*} newValue - value used to update the variable content
     */
    setValueInObject(varName, newValue, inputType = null, automated = false) {
      if (inputType === "photo") {
        this.updatePhoto(newValue);
      } else {
        if (inputType === "custom_fields") {
          _.set(this.objectData.custom_fields, varName, newValue);
        } else if (inputType === "blockage_specific_fields") {
          _.set(this.objectData.specific_fields, varName, newValue);
        } else {
          _.set(this.objectData, varName, newValue);
        }
        this.checkRequiredFieldsAreFilled();
        if (this.actionType === "edit") {
          if (inputType === "custom_fields") {
            _.set(this.dataToSend.custom_fields, varName, newValue);
          } else if (inputType === "blockage_specific_fields") {
            _.set(this.dataToSend.specific_fields, varName, newValue);
          } else {
            varName = this.getDataToSendVarName(varName);
            _.set(this.dataToSend, varName, newValue);
          }

          if (!this.dataWasChanged && !automated) this.dataWasChanged = true;
        }
      }
    },

    getDataToSendVarName(varName) {
      let updatedVarName = varName;
      if (varName.startsWith(this.initials)) {
        updatedVarName = varName.replace(this.initials, "");
      } else if (varName.startsWith("address.ad_")) {
        updatedVarName = varName.replace("address.ad_", "address.");
      } else {
        let differentVarName = this.getDifferentVarName(varName);
        if (differentVarName) {
          updatedVarName = differentVarName;
        }
      }
      return updatedVarName;
    },

    getDifferentVarName(varName) {
      return false;
    },

    //////////////////////////
    /* Save related methods */
    //////////////////////////

    /**
     * Saves the objectData and closes the form.
     */
    saveObject() {
      this.checkRequiredFieldsAreFilled();
      if (this.dataReceived.cl_logical_type !== 'FB' && !this.required.filled) {
        return;
      }

      this.loadingSave = true;
      switch (this.actionType) {
        case "create":
          this.createObject();
          break;
        case "edit":
          if (this.dataWasUpdated) {
            this.prepareEditedDataToSend();
            this.editObject();
          } else {
            this.closeForm();
          }
          break;
        default:
          this.closeForm();
      }
    },

    /* Create Methods */
    createObject() {},

    /**
     * Check if all the required fields are filled.
     */
    checkRequiredFieldsAreFilled() {
      let filled = true;
      let stillNeedFilled = [];

      if (this.required.fields.length > 0) {
        for (const field of this.required.fields) {
          let type = null;
          if (field.code) {
            type = "customfield";
          }
          if (field.blockage_type) {
            type = "blockage_specific_fields";
          }
          let fieldValue = this.getValueInObject(
            field.var_name ? field.var_name : field.uuid,
            null,
            type
          );
          if (
            fieldValue === undefined ||
            fieldValue === "" ||
            fieldValue === null
          ) {
            filled = false;
            let tab = field.tab ? field.tab.label : "Custom Field";
            stillNeedFilled.push(tab + ": " + field.label);
          }
        }
      }
      this.required.stillNeedFilled = stillNeedFilled;
      this.required.filled = filled;
    },

    /**
     * Finish the process after the object creation.
     */
    finishObjectCreation() {
      this.updateObjectLocalStorageData();
      this.$toasted.global.toast_success(this.$i18n.t("CreationSuccess"));
      this.updateGeoServer();
      this.closeForm();
    },

    updateObjectLocalStorageData() {
      sessionStorage.setItem(
        this.form.local_storage_name,
        JSON.stringify(this.objectData)
      );
    },

    /* Edit Methods */

    prepareEditedDataToSend() {
      this.dataToSend.uuid = this.dataReceived[this.initials + "uuid"];
      if (
        this.form.endpoint === "technicalpoints" ||
        this.form.endpoint === "closures" ||
        this.form.endpoint === "cables" ||
        this.form.endpoint === "pathways" ||
        this.form.endpoint === "technicalsites"
      ) {
        if (this.accessoriesSelected || this.othersSelected) {
          this.dataToSend.accessories = [];
          this.generateAccessoriesDataSend(this.accessoriesSelected);
          this.generateAccessoriesDataSend(this.othersSelected);
        }
      }

      if (
        this.form.endpoint === "technicalpoints" &&
        this.form.layer === "fc_manhole"
      ) {
        this.dataToSend.masks = this.$store.getters.getManholeMasks;
      }

      if (this.form.endpoint === "blockage") {
        this.dataToSend.specific_fields = this.objectData.specific_fields;
      }
    },

    /**
     * Saves the objectData and closes the form.
     */
    async editObject() {
      try {
        if (this.hasPhotosTab && this.imagesToRemove.length > 0) {
          await this.removePhotos();
        }
        await this.EDIT_OBJECTS({
          endpoint: this.form.endpoint,
          data: [this.dataToSend]
        });
        let connectionsArray = [];
        if (this.updatedSplicingConnections) {
          let visibleData = localStorage.getItem("visibleData");
          if(visibleData && visibleData === "insidePlan"){
            connectionsArray = this.getChangedConnectionsPhysical(this.initialConnections, this.updatedSplicingConnections);
          } else {
            connectionsArray = this.getChangedConnections(this.initialConnections, this.updatedSplicingConnections);
          }
          await this.updateObjectSplicingConnections({
            objectEndpoint: this.form.endpoint,
            objectUUID: this.objectData[this.initials + "uuid"],
            updatedConnections: { connections: connectionsArray }
          });
        }

        this.updateGeoServer();
        this.closeForm();
        this.$toasted.global.toast_success(this.$i18n.t("EditionSuccess"));
      } catch (error) {
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.loadingSave = false;
      }
    },
    getChangedConnections(initialConnections, updatedSplicingConnections){
      const newArrayConnections = [];
      updatedSplicingConnections.forEach(updatedConnection => {
        //if connection is fiber to fiber, then add updated connections and created connections
        //only the connection that have origin and destination are the ones that were updated or added
        if(!this.connectionHasPort(updatedConnection) && updatedConnection.origin.id && updatedConnection.destination.id){
          //if connection has property port_connection, delete it
          if(updatedConnection.hasOwnProperty('port_connection')){
            delete updatedConnection.port_connection
          }
          //check if origin exists in initialConnections, if it does, check for destinations, if does not exists
          //then the connection was created
          const connection = initialConnections.find(item => item.origin.id === updatedConnection.origin.id);
          if(connection){
            if(updatedConnection.destination.id !== connection.destination.id){
              const connectionCopy = {...connection};
              //clear origin and destination on old connection
              connectionCopy.origin.id = null;
              connectionCopy.destination.id = null;
              //add old connection with empty origin and destination
              newArrayConnections.push(connectionCopy);
              //connection was updated
              newArrayConnections.push(updatedConnection);
            }
          }else{
            //connection was created
            newArrayConnections.push(updatedConnection);
          }
        }else if(this.connectionHasPort(updatedConnection)){
          //add connection that has port
          newArrayConnections.push(updatedConnection);
          //check if origin exists in initial connctions
          const connectionFound = initialConnections.find(connection => connection.origin.id === updatedConnection.origin.id)
          //if connection is found, clear origin and destination only when initial connection was fiber to fiber
          if(connectionFound && connectionFound.origin.type === 'fiber' && connectionFound.destination.type === 'fiber'){
            //clean origin and destination
            const destination = {
              id: null,
              logical_type: null,
              parent: null,
              type: null,
              uuid: null
            };
            const origin = {
              id: null,
              logical_type: null,
              parent: null,
              type: null,
              uuid: null
            };
            connectionFound.origin = origin;
            connectionFound.destination = destination;
            //add deleted connection
            newArrayConnections.push(connectionFound)
          }
        }
      });
      //add deleted connections
      initialConnections.forEach(initialConnection => {
        if(!this.connectionHasPort(initialConnection) && initialConnection.origin.id && initialConnection.destination.id){
          //if origin exists in initialConnection but it does not exists in updatedConnections, then the connection was deleted
          const connection = updatedSplicingConnections.find(item => item.origin.id === initialConnection.origin.id);
          if(!connection){
            //clean origin and destination
            const destination = {
              id: null,
              logical_type: null,
              parent: null,
              type: null,
              uuid: null
            };
            const origin = {
              id: null,
              logical_type: null,
              parent: null,
              type: null,
              uuid: null
            };
            initialConnection.origin = origin;
            initialConnection.destination = destination;
            //add deleted connection
            newArrayConnections.push(initialConnection)
          }
        }
      });
      return newArrayConnections;
    },
    getChangedConnectionsPhysical(initialConnections, updatedSplicingConnections){
      const newArrayConnections = [];
      updatedSplicingConnections.forEach(updatedConnection => {
      //add deleted connections
      initialConnections.forEach(initialConnection => {
        if(!this.connectionHasPort(initialConnection) && initialConnection.origin.id && initialConnection.destination.id){
          //if origin exists in initialConnection but it does not exists in updatedConnections, then the connection was deleted
          const connection = updatedSplicingConnections.find(item => item.origin.id === initialConnection.origin.id && item.tray.id === initialConnection.tray.id);
          if(!connection){
            //clean origin and destination
            const destination = {
              id: null,
              logical_type: null,
              parent: null,
              type: null,
              uuid: null
            };
            const origin = {
              id: null,
              logical_type: null,
              parent: null,
              type: null,
              uuid: null
            };
            initialConnection.origin = origin;
            initialConnection.destination = destination;
            //add deleted connection
            newArrayConnections.push(initialConnection)
          }
        }
      });
        //if connection is fiber to fiber, then add updated connections and created connections
        //only the connection that have origin and destination are the ones that were updated or added
        if(!this.connectionHasPort(updatedConnection) && updatedConnection.origin.id && updatedConnection.destination.id){
          //check if origin exists in initialConnections, if it does, check for destinations, if does not exists
          //then the connection was created
          const connection = initialConnections.find(item => item.origin.id === updatedConnection.origin.id);
          if(connection){
            if(updatedConnection.destination.id !== connection.destination.id){
              const connectionCopy = {...connection};
              //clear origin and destination on old connection
              connectionCopy.origin.id = null;
              connectionCopy.destination.id = null;
              //add old connection with empty origin and destination
              newArrayConnections.push(connectionCopy);
              //connection was updated
              newArrayConnections.push(updatedConnection);
            }
          }else{
            //connection was created
            newArrayConnections.push(updatedConnection);
          }
        }else if(this.connectionHasPort(updatedConnection)){
          //add connection that has port
          newArrayConnections.push(updatedConnection);
        }
      });

      return newArrayConnections;
    },

    //check if connection has a port
    connectionHasPort(connection){
      return connection.origin.type === 'port' || connection.destination.type === 'port';
    },

    ////////////////////////
    /* Leave Form methods */
    ////////////////////////

    /**
     * Clears all the form data.
     */
    clearData() {
      let currentTabFields = this.visibleTabs[this.activeTab].fields;
      for (let field of currentTabFields) {
        this.setValueInObject(field.var_name, null);
      }
    },

    /** Emits an event to close this component's window **/
    closeForm() {
      this.loadingSave = false;
      bus.$emit("enable-click-events");
      this.$emit("close");
    },

    /** Emits an event update the map **/
    updateGeoServer() {
      this.$emit("update-geoserver");
    },

    /**
     * Change view mode of the object. 'View' <-> 'Edit'
     */
    changeViewModeOfObject(viewType) {
      this.structureAndPopulateForm();
      this.$emit("changeViewModeOfObject", viewType);
    },

    /**
     * change the locker (open/close)
     */
    changeLabelMode(){
      this.objectData[this.initials + 'label_locked'] = !this.objectData[this.initials + 'label_locked']
      this.setValueInObject(this.initials + 'label_locked',this.objectData[this.initials + 'label_locked'] )
    },
    /**
     * change view mode and edit mode of the object. 'View' || 'Edit' <-> 'Refresh' || 'Details'
     */
    openDetails(){
      this.$emit("openDetails");
    },
    /**
     * change view mode and edit mode of the object. 'View' || 'Edit' <-> 'Refresh' || 'Details'
     */
     refresh(){
      this.$emit("refresh");
    },

    ////////////////////
    /* Photos methods */
    ////////////////////

    /**
     * Create a list of images to be used in the photos tab to generate the photo fields.
     */
    getImagesList() {
      let photosTab = this.form.tabs.find(tab => tab.type === "photos");
      let imagesList = [];
      for (const photoField of photosTab.fields) {
        imagesList.push({
          label: photoField.label,
          varName: photoField.var_name
        });
      }
      let extraImagesList = this.addExtraPhotosToImagesList();
      this.imagesList = imagesList.concat(extraImagesList);
    },

    /**
     * Generator of photos extra fields.
     * @returns {Array}
     */
    addExtraPhotosToImagesList() {
      let extraImagesList = [];
      for (let i = 1; i <= 10; i++) {
        extraImagesList.push({
          label: "Extra " + i,
          varName: "photoExtra" + i
        });
      }
      return extraImagesList;
    },

    /**
     * Stores the information about the images that existed before editing the object.
     */
    getPreviousImages() {
      let previousImages = [];
      if (this.actionType !== "create" && this.dataReceived && this.dataReceived.images) {
        previousImages = Array.from(this.dataReceived.images);
      }
      this.previousImages = previousImages;
    },

    /**
     * Updates the information of a given photo.
     * @param {Object} photoData
     */
    updatePhoto(photoData) {
      let name = photoData.name;
      let attribute;
      let newData;
      if (photoData.hasOwnProperty("photo")) {
        attribute = "image";
        newData = photoData.photo;
      } else if (photoData.hasOwnProperty("description")) {
        attribute = "description";
        newData = photoData.description;
      }
      let images = [];
      if (this.objectData.hasOwnProperty("images")) {
        images = Array.from(this.objectData.images);
        let index = images.findIndex(i => i.name === name);
        if (index !== -1) {
          images[index][attribute] = newData;
          this.objectData.images = Array.from(images);
          return;
        }
      }
      images.push({ name: name, [attribute]: newData });
      this.objectData.images = Array.from(images);
    },

    /**
     * Add photo to array of photos to remove.
     * @param {String | Object} e
     */
    addToPhotosToRemove(e) {
      let imageName;
      if (typeof e === "string") {
        imageName = e;
      } else {
        imageName = e.name;
        this.imagesToRemove.push(e);
      }
      let imageIndex = this.objectData.images.findIndex(
        image => image.name === imageName
      );
      if (imageIndex >= 0) {
        this.objectData.images.splice(imageIndex, 1);
      }
    },

    /**
     * Removes photo from object.
     */
    async removePhotos() {
      try {
        let photos = this.imagesToRemove;
        for (let i = 0; i < photos.length; i++) {
          await this.DELETE_PHOTO(photos[i]);
        }
      } catch (error) {
        console.error(error);
      }
    },

    /////////////////////////
    /* Labelling methods */
    /////////////////////////

    /**
     * Increments 1 to the newly created label and save it in local storage.
     * @param {String} label
     */
    incrementLabelSequential(label) {
      if (label) {
        let index = 0;
        let number = "";
        for (let i = 0; i < label.length; i++) {
          let c = label.charAt(i);
          if (c >= "0" && c <= "9") {
            number += c;
          } else {
            index = i;
            number = "";
          }
        }
        label = label.substring(0, index + 1) + (Number(number) + 1);
      }
      return label;
    },

    /**
     * Gets the automatic label of an object.
     * @param {Object} request
     */
    getAutomaticLabel(request) {
      this.GET_AUTOMATIC_LABEL(request)
        .then(result => {
          this.objectData[this.initials + "label"] = result.data.data;
        })
        .catch(error => {
          this.$toasted.global.toast_error(error);
        });
    },

    /////////////////////////
    /* Coordinates methods */
    /////////////////////////

    /**
     * Filter a coordinate from an object.
     * @param {Number} coordinateIndex
     */
    getCoordinate(coordinateIndex) {
      let finalCoordinate = undefined;
      let text_geom = undefined;
      if (this.actionType !== "create") {
        text_geom = this.getTextGeom();
        finalCoordinate = this.transformCoordinate(text_geom, coordinateIndex);
      } else {
        text_geom = this.wkt;
        finalCoordinate = this.transformCoordinate(text_geom, coordinateIndex);
        let coordinate = new Point([
          coordinateIndex === 0 ? parseFloat(finalCoordinate) : 0,
          coordinateIndex === 0 ? 0 : parseFloat(finalCoordinate)
        ]);
        finalCoordinate = coordinate.transform("EPSG:3857", "EPSG:4326")
          .flatCoordinates[coordinateIndex];
      }

      return parseFloat(finalCoordinate).toFixed(6);
    },

    getTextGeom() {
      let textGeomFromObject = this.initials + "text_geom";
      if (this.objectData[textGeomFromObject]) {
        return this.objectData.textGeomFromObject;
      } else if (this.objectData.node) {
        return this.objectData.node.nd_text_geom;
      } else if (this.objectData.technicalsite) {
        return this.objectData.technicalsite.node.nd_text_geom;
      }
    },

    /**
     * Filter x or y from coordinate.
     * @param {*} textGeom
     * @param {*} coordinateIndex
     */
    transformCoordinate(textGeom, coordinateIndex) {
      if (textGeom) {
        return textGeom.match(/-?\d+.\d+/g)[coordinateIndex];
      }
    },

    ///////////////////////////////
    /* Create TL from TS methods */
    ///////////////////////////////

    /**
     * Closes the current form and opens the form to create a technical local.
     * @param {Object} technicalSite - technical site of origin
     */
    createTechnicalLocal(technicalSite) {
      this.closeForm();
      this.dialog = false;
      this.$emit(
        "create-technical-local",
        technicalSite,
        this.technicalSiteData
      );
    },

    /**
     * Closes the dialog box and he current form.
     */
    closeDialog() {
      this.finishObjectCreation();
      this.dialog = false;
    },

    ///////////////////////////////
    /* Stock Material methods */
    ///////////////////////////////

    async loadMaterialOptions({ callback }) {
      try {
        let result = await this.fetchStockMaterialsList(
          this.materialRequestQueryParameters
        );
        let options = _.cloneDeep(result);
        options.sort((a, b) => {
          let nameA = a.sm_short_description
            ? a.sm_short_description
            : a.sm_description;
          let nameB = b.sm_short_description
            ? b.sm_short_description
            : b.sm_description;
          nameA = JSON.stringify(nameA).toLowerCase();
          nameB = JSON.stringify(nameB).toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        this.materialOptions = options;
      } catch (e) {
        console.error(e);
      } finally {
        callback();
      }
    },

    materialOptionsNormalizer(node) {
      let label = node.sm_description;
      if (node.sm_short_description) {
        label = node.sm_short_description;
      }
      return {
        id: node.sm_id,
        label: label
      };
    },

    ///////////////////////////////
    /* Tabs methods */
    ///////////////////////////////

    /**
     * Defines whether the tab will be enable or disable, but it is in the list of tabs
     *
     * @param tab
     * @return {boolean}
     */
    tabIsEnabled(tab) {
      return true;
    },

    /**
     * Defines whether the tab will be in the list of tabs
     *
     * @param tab
     * @return {boolean}
     */
    tabIsVisible(tab) {
      return true;
    },

    ///////////////////
    /* Other methods */
    ///////////////////

    focusFirstInput() {
      let currentTab = this.visibleTabs[this.activeTab];

      if (currentTab) {
        let fields = currentTab.fields;
        let fieldIndex;

        if (fields) {
          fieldIndex = fields.findIndex(
            field =>
              (field.input_type === "text" ||
                field.input_type === "number" ||
                field.input_type === "int" ||
                field.input_type === "float") &&
              !field.disable
          );

          if (fieldIndex >= 0) {
            let field = fields[fieldIndex];
            let inputField;
            let varName = field.var_name.replace(".", "");
            inputField = document.querySelector("#" + varName);
            if (inputField) {
              inputField.focus();
            }
          }
        }
      }
    },

    includeActivitiesTab(visibleTabs) {
      if (
        this.userHasModulePermission("module_field_management") &&
        this.actionType !== "create"
      ) {
        let tab = {
          label: this.$i18n.tc("Activity", 2),
          type: "module",
          disable: false,
          code: "activities",
          visible: true,
          enabled: true,
          view_permission:{
            name:"field_activity_show&field_activity_store"
          }
        };
        visibleTabs.push(tab);
      }
    },
    /**
     * If there are no items in custom_fields object array tab will not appear.
     * @param {Array} visibleTabs - previously added tabs
     */
    includeCustomFieldsTab(visibleTabs) {
      if (
        this.customFields &&
        this.customFields.custom_fields &&
        this.customFields.custom_fields.length > 0
      ) {
        let tab = {
          label: this.$i18n.tc("CustomFields", 2),
          type: "module",
          disable: false,
          code: "custom_fields",
          visible: true,
          enabled: true
        };
        visibleTabs.push(tab);
      }
    },

    getFormTabByCode(form, tabCode) {
      return form.tabs.find(tab => tab.code == tabCode);
    },

    getTabFieldByName(tab, fieldName) {
      return tab.fields.find(field => field.var_name.includes(fieldName));
    },

    getFilteredListOptions(listVar, listName, listFilters) {
      this.GET_LIST_FILTERED({
        listName: listName,
        listFilters: listFilters
      })
        .then(listFiltered => {
          this[listVar] = listFiltered;
        })
        .catch(error => {
          console.error(error);
        });
    },

    userCanEditTabContent(tab) {
      if (tab.hasOwnProperty("edit_permission") && tab.edit_permission) {
        return this.userHasFibercloudPermission(tab.edit_permission.name);
      }
      return !tab.disable;
    },

    getVarNameSurface() {
      let field;
      if (this.initials === "pw_" || this.initials === "ts_") {
        field = this.initials + "surface";
      } else {
        field = this.initials + "surface_type";
      }
      return field;
    },

    updateDependentFieldValueAndDisabledState(varName, dependsField, fields) {
      this.updateDependentFieldValueInObject(varName, dependsField);
      this.updateDependentFieldDisableValue(varName, dependsField, fields);
    },

    updateDependentFieldValueInObject(varName, dependsField) {
      if (!dependsField) {
        this.setValueInObject(varName, null);
      }
    },

    updateDependentFieldDisableValue(varName, value, fields) {
      let dependentField = fields.find(field => field.var_name === varName);
      if (dependentField) {
        dependentField.disable = !value;
      }
    },

    updateCheckboxValue(e, specific_fields = null) {
      let inputType = specific_fields ? specific_fields : null;
      this.setValueInObject(e.varName, e.newValue, inputType);
    },

    generateAccessoriesAndOthersSelected(
      accessoriesSelected,
      otherSelected,
      accessories
    ) {
      if (accessories) {
        for (let item of accessories) {
          if (item.sm_family_id === "ac") {
            accessoriesSelected.push(item);
          } else {
            otherSelected.push(item);
          }
        }
      }
    },

    generateAccessoriesDataSend(list) {
      if (list) {
        for (let accessory of list) {
          this.dataToSend.accessories.push({
            id: accessory.sm_id,
            quantity: accessory.quantity
          });
        }
      }
    },

    async getStockMaterial(material) {
      return await this.GET_STOCK_MATERIAL({
        uuid: material.sm_uuid,
        append: "family_specific_fields,others"
      });
    },

    updateMaterialDependentFields(materialID) {
      this.accessoriesSelectedByMaterialId = [];
      this.othersSelectedByMaterialId = [];
      if (materialID && this.materialOptions) {
        let materialOption = this.materialOptions.find(
          material => material.sm_id === materialID
        );
        if (materialOption) {
          this.getStockMaterial(materialOption)
            .then(material => {
              if (material.others) {
                this.generateAccessoriesAndOthersSelected(
                  this.accessoriesSelectedByMaterialId,
                  this.othersSelectedByMaterialId,
                  material.others
                );
              }
            })
            .catch(error => {
              console.error(error);
            });
        }
      }
    },

    async getWorkspacePhaseValue() {
      try {
        let workspace = await this.fetchWorkspaceByUUID({
          uuid: this.$store.getters.workspace.uuid,
          includes: "engineering_rule",
          fields: "wp_uuid,wp_status,wp_id,wp_engineering_rule_id"
        });
        return workspace.wp_status;
      } catch (error) {
        console.error(error);
      }
    },
    /**
     * @returns - minNumberHPsPerMDU from network Rules
     */
    async getNetworkRuleMDU() {
      try {
        let workspace = await this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
          type: "id",
          value: sessionStorage.getItem("workspace_rules_id")
        });
        this.minNumberHPsPerMDU = workspace.data[0].data.mdu_classifier;
      } catch (error) {
        console.error(error);
      }
    },

    fieldCanBeAutofilled(varName) {
      let fieldsNotToAutofill = [
        "bl_type",
      ];
      return !fieldsNotToAutofill.includes(varName);
    }
  }
};



// WEBPACK FOOTER //
// ./src/mixins/forms/dynamicObjectForms/dynamicObjectFormsMixin.js